import usePrescriptionsRequest from '@hooks/query/e-prescriptions/usePrescriptionsRequest';
import getFullNameFromProfile from '@lib/utils/names/getFullNameFromProfile';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Drawer, Empty, ListItem, Loader } from '@ui';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { IPrescriptionTableItem } from 'types/e-prescription.types';
import PrescriptionsPatientsListItem from './prescriptions-patient-list-item';

const QUERY_PARAMS = {
  direction: 'DESC',
  sortBy: 'createdAt',
  pending: true,
} as const;

interface PrescriptionsPatientsListProps {
  item?: IPrescriptionTableItem;
  visible?: boolean;
  onClose?: () => void;
}

const PrescriptionsPatientsList: React.FunctionComponent<PrescriptionsPatientsListProps> = ({
  item,
  onClose,
  visible,
}) => {
  const { data, hasNextPage, fetchNextPage, isFetchingNextPage, original } =
    usePrescriptionsRequest(item?.client.id, QUERY_PARAMS);
  const parentRef = React.useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? data.length + 1 : data.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 65,
    overscan: 5,
  });

  const items = rowVirtualizer.getVirtualItems();

  React.useEffect(() => {
    const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

    if (!lastItem) {
      return;
    }

    if (lastItem.index >= data.length - 1 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage, data.length, isFetchingNextPage, items]);

  return (
    <Drawer
      visible={visible ?? true}
      onClose={onClose}
      destroyOnClose
      isInfiniteScroll
      ref={parentRef}
      title={`${getFullNameFromProfile(item?.client.profile)} (${
        original?.pages[0]?.pagination.total ?? 0
      })`}
    >
      {data.length === 0 ? (
        <Empty
          title={<FormattedMessage id="prescriptions.prescriptionsList.emptyTitle" />}
          style={{ padding: 32 }}
        />
      ) : (
        <Drawer.Scroller totalHeight={rowVirtualizer.getTotalSize()} startPoint={items[0].start}>
          <ListItem.Group>
            {items.map((virtualRow) => {
              const isLoaderRow = virtualRow.index > data.length - 1;
              const item = data[virtualRow.index];
              const extra = {
                key: virtualRow.key,
                'data-index': virtualRow.index,
                ref: rowVirtualizer.measureElement,
              };

              return (
                <>
                  {isLoaderRow ? (
                    hasNextPage ? (
                      <div className="load-more" {...extra}>
                        <Loader size="small" />
                      </div>
                    ) : (
                      <div className="load-more" {...extra}>
                        Načítať viac
                      </div>
                    )
                  ) : (
                    <PrescriptionsPatientsListItem item={item} {...extra} />
                  )}
                </>
              );
            })}
          </ListItem.Group>
        </Drawer.Scroller>
      )}
    </Drawer>
  );
};

export default React.memo(PrescriptionsPatientsList);
